import { useMemo, useState } from 'react';
import classnames from 'classnames';
import { Tooltip } from '@ds';
import { MapFilterType } from '@lp/features/RoutePlanner/types';
import { MARKER_TYPE_ICONS } from '@lp/features/RoutePlanner/config';
import { ConflictReservationInfo, MatchCell, OrgansLastCall } from '@lp/features/AssignmentTables/components';
import { useCaseDetails } from '@/hooks/useCaseDetails';
import { CaseMatchType, CSAssignmentCandidate } from '@/api/apis/resources';
import { ResourceName } from './ResourceName';

type HubCSTooltipProps = {
  list: CSAssignmentCandidate[];
  title: string;
};

export const HubCSTooltip = ({ list, title }: HubCSTooltipProps) => {
  const { caseName } = useCaseDetails();
  const [isOpen, setIsOpen] = useState(false);

  const reservedListNumber = useMemo(() => {
    return list.filter((i) => i.match.caseType !== CaseMatchType.AssignmentCandidate).length;
  }, [list]);

  return (
    <Tooltip
      placement="bottom"
      onOpenChange={(value) => setIsOpen(value)}
      showArrow
      closeDelay={0}
      classNames={{
        base: 'group',
        content: 'shadow-xl opacity-90',
      }}
      content={
        <div className="py-2 flex flex-col gap-2">
          <div className="flex gap-1">
            <span className="text-m-sb text-black">{title}</span>
            <span className="text-m-r text-black">(Clinical Specialists)</span>
          </div>
          {list.map((i, index) => (
            <div
              key={i.employeeId}
              className={classnames('flex gap-2 items-center py-1 px-2 rounded-lg', {
                'bg-base-80': index % 2,
                'bg-white': !(index % 2),
                'opacity-70': i.match.caseType !== CaseMatchType.AssignmentCandidate,
              })}
            >
              <ResourceName fullName={i.employeeFullName} photoPath={i.employeeProfilePhoto} />
              <OrgansLastCall
                shiftCertificates={i.shiftCertificates}
                employeeCertificates={i.employeeCertificates}
                lastCallDaysByOrgan={i.lastCallDays.forCaseDonorByOrgan}
                size="sm"
              />
              <div className="ml-auto flex gap-2">
                <MatchCell matchType={i.match.matchType} />
                <ConflictReservationInfo
                  reservation={i.conflictedReservation}
                  caseTypeMatch={i.match.caseType}
                  caseName={caseName}
                  isColumn
                />
              </div>
            </div>
          ))}
        </div>
      }
    >
      <div className="absolute -bottom-2 right-4 flex">
        <div
          className={`rounded-3xl py-1.5 px-2 flex gap-2 items-center text-base-5 shadow-xl hover:bg-base-60 ${isOpen ? 'bg-base-60' : 'bg-white'}`}
        >
          {MARKER_TYPE_ICONS[MapFilterType.clinicalSpecialists]}
          <span className="text-s-sb text-base-5">{list.length - reservedListNumber}</span>
          {!!reservedListNumber && (
            <span className="block bg-base-70 px-1 text-s-sb text-base-30 rounded-md">{reservedListNumber}</span>
          )}
        </div>
      </div>
    </Tooltip>
  );
};
